<template>
  <AlertBox v-if="modelValue" @closeAlert="$emit('update:modelValue', false)" width="500px">
    <div class="share-box">
      <h4>Share Specifications</h4>
      <p>
        Copy the following link to share this lens specification with others. The link will expire
        in 30 days.
      </p>
      <div class="spinner-box" v-if="loading">
        <div class="spinner-load"></div>
      </div>
      <div class="share-link-box" @click="copyShareLink" v-else>
        <div class="share-link">{{ shareableLink }}</div>
        <img src="@/assets/img/copy.svg" class="copy-icon" alt="Copy" v-if="!copySuccess" />
        <img
          v-else
          src="@/assets/img/copy-checked.svg"
          alt="copy-success"
          class="copy-icon"
          :class="{ bounce: copySuccess }"
        />
      </div>
    </div>
  </AlertBox>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted } from 'vue'
import AlertBox from './AlertBox.vue'
import { useToastStore } from '@/stores/Toast'
import { getLensShareLink } from '@/api/share-lens'

const toast = useToastStore()
const loading = ref(false)

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  select: {
    type: Object,
    required: true
  }
})

defineEmits(['update:modelValue'])

const copySuccess = ref(false)
let copyTimeout = null
let debounceTimeout = null

const shareableLink = ref(null)

const copyShareLink = async () => {
  if (debounceTimeout) return // Prevent rapid multiple copies

  try {
    await navigator.clipboard.writeText(shareableLink.value)
    copySuccess.value = true

    clearTimeout(copyTimeout)
    copyTimeout = setTimeout(() => {
      copySuccess.value = false
    }, 3000)

    debounceTimeout = setTimeout(() => {
      debounceTimeout = null
    }, 500)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const fetchShareLink = async () => {
  try {
    loading.value = true
    const payload = {
      lensId: props.select.lensId,
      sceneType: props.select.sceneImageType
    }
    const response = await getLensShareLink(payload)
    if (response.error) {
      console.error('Error fetching lens share link:')
      throw new Error()
    }
    const hash = response.data?.hash
    shareableLink.value = `${window.location.origin}/shared?hash=${hash}`
  } catch (error) {
    console.error('Error fetching lens share link:', error)
    toast.showToast('error', 'Failed to get lens share link!')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchShareLink()
})
</script>

<style scoped>
.share-box p {
  margin-bottom: 20px;
}
.share-link-box {
  display: flex;
  gap: 10px;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 15px;
  border-radius: 5px;
  cursor: pointer;
  position: relative;
  padding-right: 40px;
}

.share-link {
  flex: 1;
  opacity: 0.8;
  font-size: 14px;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-icon {
  width: 20px;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.2s;

  /* used absolute as it caused stuttering effect on change */
  position: absolute;
  right: 10px;
  margin-bottom: 3px;
}

.copy-icon:hover {
  opacity: 1;
}

.copy-icon {
  transition: all 0.3s ease;
}

.copy-icon.bounce {
  opacity: 1;
}

.spinner-box {
  display: flex;
  justify-content: center;
}
</style>
